// Extracted from original/src/App.tsx:46005-46110 (the "رمز السوبر" security-check modal, ADR-08 §2.1;
// reference screenshot documentation/01_visual_reference/states/security_super_code_dialog.png). Kept:
// backdrop, dark header with ShieldCheck, amber message box, label + centred password input, error line,
// Cancel / Confirm buttons. Changed (D-018, D-133, see DEVIATIONS DEV-05): the shared code becomes the user's
// own 6-digit PIN; an optional supervisor section (username + PIN on the same screen) replaces the "master"
// variant. Cut: securityPassword / confirmSecurityAction state and the stored-code comparison.
import { ShieldCheck, X } from 'lucide-react';
import { motion } from 'motion/react';
import { useT } from '../i18n-context.js';

export interface PinDialogProps {
  readonly open: boolean;
  /** Optional context line (the old `securityCheckMessage`). */
  readonly message?: string | null;
  readonly pin: string;
  readonly onPinChange: (value: string) => void;
  /** When true the supervisor fields are shown instead of the own-PIN field (D-018). */
  readonly supervisorMode: boolean;
  /** Whether the screen offers the supervisor option at all (route access `own_or_supervisor`). */
  readonly allowSupervisor: boolean;
  readonly onSupervisorModeChange: (value: boolean) => void;
  readonly supervisorUsername: string;
  readonly onSupervisorUsernameChange: (value: string) => void;
  readonly supervisorPin: string;
  readonly onSupervisorPinChange: (value: string) => void;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
  readonly error?: string | null;
  readonly busy?: boolean;
}

const PIN_INPUT = "w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-rose-500/20 text-xl font-black text-center dark:text-[#f0f6fc] font-mono transition-all";
const LABEL = "block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest";

export function PinDialog(props: PinDialogProps) {
  const t = useT();
  if (!props.open) return null;
  const ready = props.supervisorMode ? props.supervisorUsername.trim().length > 0 && props.supervisorPin.length === 6 : props.pin.length === 6;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="pin-dialog">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={props.onCancel}
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
      />
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-[#0d1117] w-full max-w-md rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border"
      >
        <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-gray-900 text-white dark:bg-[#161b22]">
          <h3 className="text-xl font-black flex items-center gap-2 uppercase tracking-tight">
            <ShieldCheck className="text-rose-500" />
            {props.supervisorMode ? t('pin.title_supervisor') : t('pin.title')}
          </h3>
          <button
            type="button"
            onClick={props.onCancel}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (ready && !props.busy) props.onConfirm();
          }}
          className="p-6 space-y-4"
        >
          {props.message && (
            <div className="p-4 bg-amber-50 dark:bg-amber-950/20 rounded-2xl text-xs font-bold text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-900/30">
              {props.message}
            </div>
          )}
          {!props.supervisorMode ? (
            <div>
              <label className={LABEL}>{t('pin.enter_own')}</label>
              <input
                type="password"
                inputMode="numeric"
                maxLength={6}
                autoFocus
                autoComplete="off"
                data-testid="pin-own"
                value={props.pin}
                onChange={(e) => props.onPinChange(e.target.value)}
                placeholder="••••••"
                className={PIN_INPUT}
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className={LABEL}>{t('pin.supervisor_username')}</label>
                <input
                  type="text"
                  autoFocus
                  autoComplete="off"
                  data-testid="pin-supervisor-username"
                  value={props.supervisorUsername}
                  onChange={(e) => props.onSupervisorUsernameChange(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-rose-500/20 font-black dark:text-[#f0f6fc] transition-all"
                />
              </div>
              <div>
                <label className={LABEL}>{t('pin.supervisor_pin')}</label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="off"
                  data-testid="pin-supervisor-pin"
                  value={props.supervisorPin}
                  onChange={(e) => props.onSupervisorPinChange(e.target.value)}
                  placeholder="••••••"
                  className={PIN_INPUT}
                />
              </div>
            </div>
          )}
          {props.allowSupervisor && (
            <button
              type="button"
              onClick={() => props.onSupervisorModeChange(!props.supervisorMode)}
              data-testid="pin-toggle-supervisor"
              className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest hover:underline"
            >
              {props.supervisorMode ? t('pin.use_own') : t('pin.use_supervisor')}
            </button>
          )}
          {props.error && (
            <p data-testid="pin-error" className="mt-2 text-xs font-bold text-rose-500 text-center">
              {props.error}
            </p>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={props.onCancel}
              className="w-1/2 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-[#161b22] dark:hover:bg-[#21262d] text-gray-700 dark:text-gray-300 rounded-xl font-bold transition-all text-xs"
            >
              {t('pin.cancel')}
            </button>
            <button
              type="submit"
              disabled={props.busy || !ready}
              data-testid="pin-confirm"
              className="w-1/2 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-black transition-all shadow-lg shadow-rose-600/20 disabled:opacity-50 text-xs uppercase tracking-widest flex items-center justify-center gap-2"
            >
              {props.busy ? t('pin.verifying') : t('pin.confirm')}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
